import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cloud, Square, Circle, Maximize2 } from 'lucide-react';
import Button from '../components/ui/Button.tsx';
import GradientText from '../components/ui/GradientText.tsx';
import SEO from '../components/SEO.tsx';
import { Mail } from 'lucide-react';
import DemoVideoPopup from '../components/ui/DemoVideoPopup.tsx';

interface DeployProps {
  onNavigate: (page: string) => void;
}

const Deploy: React.FC<DeployProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const cloudProviders = [
    {
      name: 'AWS',
      description: 'Deploy on Amazon Web Services with full EKS support',
      features: ['EKS Integration', 'Auto-scaling', 'Load Balancing', 'CloudWatch Integration'],
      logo: Cloud
    },
    {
      name: 'Azure',
      description: 'Deploy on Microsoft Azure with AKS support',
      features: ['AKS Integration', 'Azure Monitor', 'Application Gateway', 'Azure AD Integration'],
      logo: Square
    },
    {
      name: 'Google Cloud',
      description: 'Deploy on Google Cloud Platform with GKE support',
      features: ['GKE Integration', 'Cloud Monitoring', 'Load Balancer', 'IAM Integration'],
      logo: Circle
    }
  ];

  const deploymentSteps = [
    {
      step: 1,
      title: 'Prerequisites',
      description: 'Ensure you have the required tools installed',
      items: ['kubectl', 'helm', 'Docker', 'Cloud CLI tools']
    },
    {
      step: 2,
      title: 'Configuration',
      description: 'Configure your deployment settings',
      items: ['Cluster configuration', 'Security settings', 'Resource allocation', 'Environment variables']
    },
    {
      step: 3,
      title: 'Deploy',
      description: 'Execute the deployment commands',
      items: ['Apply manifests', 'Install Helm charts', 'Verify deployment', 'Configure monitoring']
    },
    {
      step: 4,
      title: 'Verify',
      description: 'Confirm your deployment is working correctly',
      items: ['Health checks', 'Service connectivity', 'Dashboard access', 'Alert configuration']
    }
  ];

  return (
    <div className="min-h-screen pt-16">
      <DemoVideoPopup />
      <SEO
        title="Deploy Kexa Premium - Quick Start Cloud Security Setup"
        description="Get started with Kexa Premium in minutes. Follow our deployment guide to secure your AWS, Azure, GCP infrastructure with advanced compliance monitoring."
        keywords="kexa deployment, cloud security setup, infrastructure monitoring setup, AWS security deployment, compliance scanner installation"
      />
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {t('deploy.title.prefix')}
            <GradientText variant="primary">{t('deploy.title.highlight')}</GradientText>
            {t('deploy.title.middle')}
            <GradientText variant="accent">{t('deploy.title.highlightSecondary')}</GradientText>
            {t('deploy.title.suffix')}
          </h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            {t('deploy.subtitle')}
          </p>
        </div>
        <div className="bg-gray-900 rounded-2xl p-8 mb-16">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">{t('deploy.deploymentSteps.step1.title')}</h3>
              <div className="bg-black rounded-lg p-4 font-mono text-sm">
                <div className="text-green-400 mb-2">{t('deploy.deploymentSteps.step1.comments.clone')}</div>
                <div className="text-white">git clone https://github.com/4urcloud/kexa-enterprise.git</div>
                <div className="text-white">cd kexa-enterprise/docker-compose/</div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-3">{t('deploy.deploymentSteps.step2.title')}</h3>
              <div className="bg-black rounded-lg p-4 font-mono text-sm">
                <div className="text-green-400 mb-2">{t('deploy.deploymentSteps.step2.comments.keycloak')}</div>
                <div className="text-white mb-4">openssl req -x509 -nodes -days 650 -newkey rsa:2048 -keyout keycloak-scripts/certs/server.key -out keycloak-scripts/certs/server.crt</div>
                
                <div className="text-green-400 mb-2">{t('deploy.deploymentSteps.step2.comments.nginx')}</div>
                <div className="text-white mb-4">openssl req -x509 -nodes -days 650 -newkey rsa:2048 -keyout nginx-scripts/certs/nginx.key -out nginx-scripts/certs/nginx.crt</div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-3">{t('deploy.deploymentSteps.step3.title')}</h3>
              <div className="bg-black rounded-lg p-4 font-mono text-sm">
                <div className="text-green-400 mb-2">{t('deploy.deploymentSteps.step3.comments.env')}</div>
                <div className="text-white mb-4">cp .env-example .env</div>
                
                <div className="text-green-400 mb-2">{t('deploy.deploymentSteps.step3.comments.configure')}</div>
                
                <div className="text-green-400 mb-2">{t('deploy.deploymentSteps.step3.comments.start')}</div>
                <div className="text-white">docker-compose up -d</div>
              </div>
            </div>

            <div className="bg-blue-900/30 border border-blue-500/20 rounded-lg p-4">
              <p className="text-blue-200 text-sm">
                <strong>{t('deploy.deploymentSteps.note.title')}</strong> {t('deploy.deploymentSteps.note.description')}
              </p>
              <ul className="text-blue-200 text-sm mt-2 ml-4 list-disc">
                <li><code>keycloak-scripts/certs/</code> - {t('deploy.deploymentSteps.note.keycloak')}</li>
                <li><code>nginx-scripts/certs/</code> - {t('deploy.deploymentSteps.note.nginx')}</li>
              </ul>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* First Connection Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-xl md:text-2xl lg:text-5xl mb-4 font-bold">
              {t('deploy.firstConnection.title.prefix')}
              <GradientText variant="primary">{t('deploy.firstConnection.title.highlight')}</GradientText>
              {t('deploy.firstConnection.title.suffix')}
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              {t('deploy.firstConnection.description')}
            </p>
          </div>
          
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12 animate-fade-in">
            {/* Screenshot - Left side on desktop, top on mobile */}
            <div className="lg:w-1/2">
              <div className="relative group bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200/50 hover:border-primary/30 transition-all duration-500 overflow-hidden">
                <img 
                  src="/screens/tmp_first_login.png"
                  alt="First login on Kexa Premium"
                  className="w-full h-full object-cover rounded-xl transition-all duration-500 group-hover:scale-105"
                />
                <button
                  onClick={() => setFullscreenImage('/screens/tmp_first_login.png')}
                  className="absolute top-2 right-2 p-2 bg-black/20 hover:bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-sm z-10"
                  aria-label="View fullscreen"
                >
                  <Maximize2 size={16} className="text-white" />
                </button>
              </div>
            </div>
            
            {/* Content - Right side on desktop, bottom on mobile */}
            <div className="lg:w-1/2 text-center lg:text-left">
              <div className="space-y-6">
                <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                  {t('deploy.firstConnection.content.paragraph1')}
                </p>
                <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                  {t('deploy.firstConnection.content.paragraph2')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registering License Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-xl md:text-2xl lg:text-5xl mb-4 font-bold">
              {t('deploy.registeringLicense.title.prefix')}
              <GradientText variant="primary">{t('deploy.registeringLicense.title.highlight')}</GradientText>
              {t('deploy.registeringLicense.title.suffix')}
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              {t('deploy.registeringLicense.description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-fade-in">
            {/* Step 1 */}
            <div className="text-center">
              <div className="mb-8">
                <div className="bg-gradient-primary rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 shadow-button">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <h3 className="text-xl font-bold mb-2">
                  <GradientText variant="primary">{t('deploy.registeringLicense.steps.step1.title')}</GradientText>
                </h3>
                <p className="text-gray-600">
                  {t('deploy.registeringLicense.steps.step1.description')}{' '}
                  <button
                    onClick={() => onNavigate('account')}
                    className="text-primary hover:text-accent font-medium transition-colors duration-200"
                  >
                    {t('deploy.registeringLicense.steps.step1.linkText')}
                  </button>
                </p>
              </div>
              <div className="relative group bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200/50 hover:border-primary/30 transition-all duration-500 overflow-hidden max-w-md mx-auto">
                <img 
                  src="/screens/license-step1.png"
                  alt="Step 1: Retrieve license key and user ID"
                  className="w-full h-auto object-cover rounded-xl transition-all duration-500 group-hover:scale-105"
                />
                <button
                  onClick={() => setFullscreenImage('/screens/license-step1.png')}
                  className="absolute top-2 right-2 p-2 bg-black/20 hover:bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-sm z-10"
                  aria-label="View fullscreen"
                >
                  <Maximize2 size={16} className="text-white" />
                </button>
              </div>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="mb-8">
                <div className="bg-gradient-primary rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 shadow-button">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
                <h3 className="text-xl font-bold mb-2">
                  <GradientText variant="primary">{t('deploy.registeringLicense.steps.step2.title')}</GradientText>
                </h3>
                <p className="text-gray-600">
                  {t('deploy.registeringLicense.steps.step2.description')}
                </p>
              </div>
              <div className="relative group bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200/50 hover:border-primary/30 transition-all duration-500 overflow-hidden max-w-md mx-auto">
                <img 
                  src="/screens/license-step2.png"
                  alt="Step 2: Register license in your on-premise deployment"
                  className="w-full h-auto object-cover rounded-xl transition-all duration-500 group-hover:scale-105"
                />
                <button
                  onClick={() => setFullscreenImage('/screens/license-step2.png')}
                  className="absolute top-2 right-2 p-2 bg-black/20 hover:bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-sm z-10"
                  aria-label="View fullscreen"
                >
                  <Maximize2 size={16} className="text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Start First Scan Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-xl md:text-2xl lg:text-5xl mb-4 font-bold">
              {t('deploy.startFirstScan.title.prefix')}
              <GradientText variant="primary">{t('deploy.startFirstScan.title.highlight')}</GradientText>
              {t('deploy.startFirstScan.title.suffix')}
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              {t('deploy.startFirstScan.description')}
            </p>
          </div>
          
          <div className="flex justify-center animate-fade-in">
            <div className="max-w-4xl w-full">
              <div className="relative group bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200/50 hover:border-primary/30 transition-all duration-500 overflow-hidden">
                <img 
                  src="/screens/first-scan-setup.png"
                  alt="Start your first scan setup"
                  className="w-full h-full object-cover rounded-xl transition-all duration-500 group-hover:scale-105"
                />
                <button
                  onClick={() => setFullscreenImage('/screens/first-scan-setup.png')}
                  className="absolute top-2 right-2 p-2 bg-black/20 hover:bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-sm z-10"
                  aria-label="View fullscreen"
                >
                  <Maximize2 size={16} className="text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

        <div className="py-20 bg-gradient-hero relative overflow-hidden ">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 animate-slide-up">
              {t('deploy.needHelp.title')}
            </h2>
            <div className="w-16 h-px bg-white/40 mx-auto mb-8 animate-fade-in"></div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in">
            <Button
              onClick={() => onNavigate('plans')}
              variant="cta"
              size="lg"
              leftIcon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>}
            >
              {t('common.buttons.explorePlans')}
            </Button>
            <Button
              onClick={() => onNavigate('contact')}
              variant="cta-glass"
              size="lg"
              rightIcon={<Mail size={20} />}
            >
              {t('common.buttons.contactUs')}
            </Button>
            <Button
              onClick={() => onNavigate('roadmap')}
              variant="cta-glass"
              size="lg"
              rightIcon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>}
            >
              {t('common.buttons.viewRoadmap')}
            </Button>
            </div>
          </div>
        </div>

      {fullscreenImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img 
              src={fullscreenImage} 
              alt="Fullscreen view"
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute top-4 right-4 p-2 bg-black/40 hover:bg-black/60 rounded-lg backdrop-blur-sm"
              aria-label="Close fullscreen"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deploy;